import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideCopy, lucideX } from '@ng-icons/lucide';
import { HlmInputImports } from '@lite-workspace/shared/helm-ui/input';
import { HlmButton, type ButtonVariants } from '@lite-workspace/shared/helm-ui/button';
import { writeToClipboard } from './write-to-clipboard';

export type CopyBoxAppearance = 'box' | 'button' | 'field';

/** One-click credential/link copy box: the whole framed value is the copy target, a green check
 *  acknowledges the copy for a beat. Shared by the share dialog and the send-to-student explainer.
 *  appearance="button" — тот же копир с подтверждением, но без поля: там, где значение на экране
 *  не показывают (строка таблицы, футер диалога с собственной текстарией).
 *  appearance="field" — рамка со значением текстом и кнопкой копирования справа; `shownValue`
 *  показывает значение короче, чем копируется (адрес без схемы). */
@Component({
  selector: 'app-copy-box',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmInputImports, HlmButton, NgIcon, NgTemplateOutlet],
  viewProviders: [provideIcons({ lucideCheck, lucideCopy, lucideX })],
  host: { class: 'block' },
  template: `
    <ng-template #copyButton>
      <button hlmBtn size="sm" type="button" [variant]="buttonVariant()" [disabled]="disabled() || busy()" (click)="copyValue()">
        {{ actionLabel() }}
      </button>
    </ng-template>
    @if (appearance() === 'button') {
      <ng-container [ngTemplateOutlet]="copyButton" />
    } @else if (appearance() === 'field') {
      <div class="flex min-h-11 items-center gap-3 rounded-[var(--r)] border border-line2 bg-bg2 py-1 pr-1 pl-3.5">
        <span class="min-w-0 flex-1 truncate text-base text-ink">{{ shownValue() ?? value() }}</span>
        <ng-container [ngTemplateOutlet]="copyButton" />
      </div>
    } @else {
      <!-- Само значение спрятано от скринридера (input aria-hidden), поэтому его называет подпись кнопки —
           кроме случая valueInLabel=false, когда значение нечитаемо вслух (например код для вставки). -->
      <button type="button" (click)="copyValue()" [class]="boxClass" [attr.aria-label]="ariaLabel()">
        <input hlmInput readonly tabindex="-1" [value]="value()" [class]="inputClass" aria-hidden="true" />
        <span [class]="iconClass()" aria-hidden="true">
          @if (showCheck()) {
            <ng-icon name="lucideCheck" class="text-xl" strokeWidth="2.2" />
          } @else if (showError()) {
            <ng-icon name="lucideX" class="text-xl" strokeWidth="2.2" />
          } @else {
            <ng-icon name="lucideCopy" class="text-xl" />
          }
        </span>
      </button>
    }
  `,
})
export class CopyBoxComponent {
  private readonly clipboard = inject(Clipboard);

  readonly value = input('');
  readonly copyLabel = input('Скопировать');
  readonly copiedLabel = input('Скопировано');
  readonly failedLabel = input('Не удалось скопировать');
  /** Дописывать ли значение в доступное имя кнопки. Выключается там, где значение — не человеческий
   *  текст (код для вставки, длинная разметка): скринридер иначе диктует его целиком. */
  readonly valueInLabel = input(true);
  readonly appearance = input<CopyBoxAppearance>('box');
  /** Текст в рамке appearance="field"; null — показывается само `value`. */
  readonly shownValue = input<string | null>(null);
  /** Оформление кнопки в appearance="button" и "field" — совпадает с вариантами hlmBtn. */
  readonly buttonVariant = input<ButtonVariants['variant']>('default');
  readonly disabled = input(false);
  /** Значение, известное только в момент клика (за ссылкой надо сходить на сервер). null от загрузчика —
   *  копирования не было: подтверждение не показываем, а об ошибке сообщает сам вызывающий. */
  readonly valueLoader = input<(() => Promise<string | null>) | null>(null);
  /** How long the check/error icon stays after a copy. */
  readonly flashMs = input(3000);
  readonly copied = output<void>();

  protected readonly showCheck = signal(false);
  protected readonly showError = signal(false);
  protected readonly busy = signal(false);
  protected readonly actionLabel = computed(() => {
    if (this.showCheck()) return this.copiedLabel();
    if (this.showError()) return this.failedLabel();
    return this.copyLabel();
  });

  protected readonly ariaLabel = computed(() => {
    if (this.showCheck() || this.showError()) return this.actionLabel();
    return this.valueInLabel() ? `${this.copyLabel()}: ${this.value()}` : this.copyLabel();
  });

  private checkTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.checkTimer) clearTimeout(this.checkTimer);
    });
  }

  // The whole box is the copy target (a button): full-width, hover-lit, keyboard-focusable.
  protected readonly boxClass =
    'flex w-full min-h-11 cursor-pointer items-center gap-1.5 rounded-[var(--r-l)] ' +
    'border border-line2 bg-bg2 py-1.5 pl-3.5 pr-2 text-left ' +
    'transition-colors hover:border-acc-line focus-visible:border-acc-line focus-visible:outline-none';

  // hlmInput carries its own height/border/bg/padding; neutralise them so the field sits transparently
  // inside the copy box (the box provides the frame) and shows only the ellipsised value.
  // `pointer-events-none` lets a click anywhere in the box reach the surrounding copy button.
  // `dark:bg-transparent` must be explicit — it beats hlmInput's `dark:bg-input/30` (same twMerge
  // group, user classes win); plain `bg-transparent` alone leaves the grey wash behind the text.
  protected readonly inputClass =
    'pointer-events-none min-w-0 flex-1 truncate border-0 bg-transparent dark:bg-transparent ' +
    'p-0 text-sm leading-normal text-ink ' +
    'shadow-none outline-none h-auto focus-visible:ring-0 focus-visible:ring-offset-0';

  protected iconClass(): string {
    const base = 'grid size-7.5 shrink-0 place-items-center rounded-lg';
    if (this.showCheck()) return `${base} text-ok`;
    if (this.showError()) return `${base} text-bad`;
    return `${base} text-acc`;
  }

  protected async copyValue(): Promise<void> {
    const loader = this.valueLoader();
    if (loader && this.busy()) return;

    // Загрузчик зовётся и запись стартует синхронно в клике — до первого await (см. writeToClipboard).
    let written: boolean | null;
    if (loader) {
      this.busy.set(true);
      try {
        written = await writeToClipboard(loader(), this.copyByCdk);
      } finally {
        this.busy.set(false);
      }
    } else {
      written = await writeToClipboard(this.value(), this.copyByCdk);
    }

    if (written === null) return;
    if (!written) {
      this.flash('error');
      return;
    }

    this.flash('check');
    this.copied.emit();
  }

  private flash(kind: 'check' | 'error'): void {
    this.showCheck.set(kind === 'check');
    this.showError.set(kind === 'error');
    if (this.checkTimer) clearTimeout(this.checkTimer);
    this.checkTimer = setTimeout(() => {
      this.showCheck.set(false);
      this.showError.set(false);
    }, this.flashMs());
  }

  /** Синхронный execCommand CDK — запасной путь для окружений без navigator.clipboard. */
  private readonly copyByCdk = (value: string): boolean => this.clipboard.copy(value);
}
